import { NetworkInterceptor, NetworkRequestConfig, NetworkResponse, NetworkError } from '../interfaces/network.types';

/**
 * Idempotency Interceptor
 * 
 * Automatically generates and attaches a unique X-Idempotency-Key header 
 * to all state-changing requests (POST, PUT, PATCH, DELETE).
 * 
 * This ensures that if a request is retried due to network issues, 
 * the server can recognize it as the same operation and avoid duplicate processing.
 * 
 * Enterprise-Grade resilience standard.
 */
export class IdempotencyInterceptor implements NetworkInterceptor {
    private readonly STATE_CHANGING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

    onRequest(config: NetworkRequestConfig): NetworkRequestConfig {
        const method = config.method ? config.method.toUpperCase() : 'GET';

        // Only add idempotency key for state-changing methods
        if (this.STATE_CHANGING_METHODS.includes(method)) {
            const idempotencyKey = config.idempotencyKey || this.generateKey();

            return {
                ...config,
                headers: {
                    ...config.headers,
                    'X-Idempotency-Key': idempotencyKey,
                },
            };
        }

        return config;
    }

    onResponse(response: NetworkResponse): NetworkResponse {
        return response;
    }

    onError(error: NetworkError): NetworkError {
        return error;
    }

    /**
     * Generates a platform-agnostic, unique idempotency key.
     * Uses a combination of timestamp and entropy.
     */
    private generateKey(): string {
        const timestamp = Date.now().toString(36);
        const randomPart = Math.random().toString(36).substring(2, 15);
        return `${timestamp}-${randomPart}`;
    }
}
